export interface ConnectionTestResult {
  success: boolean;
  latency?: number;
  error?: string;
  timestamp: Date;
}

export class ConnectionDiagnostics {
  private supabaseUrl: string;

  constructor(supabaseUrl: string) {
    this.supabaseUrl = supabaseUrl;
  }

  async testConnection(): Promise<ConnectionTestResult> {
    const startTime = Date.now();

    try {
      console.log('🔍 Testing connection to Supabase...');

      // Test if the Supabase endpoint is reachable
      const response = await fetch(`${this.supabaseUrl}/rest/v1/`, {
        method: 'HEAD',
        headers: {
          'Content-Type': 'application/json',
        },
        signal: AbortSignal.timeout(10000) // 10 second timeout
      });

      const latency = Date.now() - startTime;

      if (response.ok || response.status === 401 || response.status === 403) {
        // 401/403 means the server is reachable, just needs authentication
        console.log(`✅ Connection successful (${latency}ms)`);
        return {
          success: true,
          latency,
          timestamp: new Date()
        };
      } else {
        console.error(`❌ Connection failed: HTTP ${response.status}`);
        return {
          success: false,
          error: `HTTP ${response.status}: ${response.statusText}`,
          latency,
          timestamp: new Date()
        };
      }
    } catch (error) {
      const latency = Date.now() - startTime;
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';

      console.error('❌ Connection test failed:', errorMessage);

      return {
        success: false,
        error: errorMessage,
        latency,
        timestamp: new Date()
      };
    }
  }

  async testAuthEndpoint(): Promise<ConnectionTestResult> {
    const startTime = Date.now();

    try {
      console.log('🔍 Testing auth endpoint...');

      const response = await fetch(`${this.supabaseUrl}/auth/v1/health`, {
        method: 'GET',
        signal: AbortSignal.timeout(10000)
      });

      const latency = Date.now() - startTime;

      if (response.ok) {
        console.log(`✅ Auth endpoint healthy (${latency}ms)`);
        return {
          success: true,
          latency,
          timestamp: new Date()
        };
      } else {
        console.error(`❌ Auth endpoint failed: HTTP ${response.status}`);
        return {
          success: false,
          error: `HTTP ${response.status}: ${response.statusText}`,
          latency,
          timestamp: new Date()
        };
      }
    } catch (error) {
      const latency = Date.now() - startTime;
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';

      console.error('❌ Auth endpoint test failed:', errorMessage);

      return {
        success: false,
        error: errorMessage,
        latency,
        timestamp: new Date()
      };
    }
  }

  async runFullDiagnostics(): Promise<{
    connection: ConnectionTestResult;
    auth: ConnectionTestResult;
    overallStatus: 'healthy' | 'degraded' | 'down';
  }> {
    console.log('🔍 Running full connection diagnostics...');

    const connection = await this.testConnection();
    const auth = await this.testAuthEndpoint();

    let overallStatus: 'healthy' | 'degraded' | 'down';

    if (connection.success && auth.success) {
      overallStatus = 'healthy';
    } else if (connection.success || auth.success) {
      overallStatus = 'degraded';
    } else {
      overallStatus = 'down';
    }

    console.log(`📊 Diagnostics complete. Status: ${overallStatus}`);

    return {
      connection,
      auth,
      overallStatus
    };
  }

  getRecommendedAction(result: ConnectionTestResult): string {
    if (result.success) {
      return 'Connection is healthy';
    }

    if (!result.error) {
      return 'Unknown connection issue. Please try again.';
    }

    if (result.error.includes('Failed to fetch') || result.error.includes('NetworkError')) {
      return 'Network error detected. Check your internet connection and try again.';
    }

    if (result.error.includes('timeout') || result.error.includes('timed out')) {
      return 'Connection timeout. The server may be slow or unreachable.';
    }

    if (result.error.includes('CORS')) {
      return 'CORS error detected. This may be a configuration issue.';
    }

    if (result.error.includes('403') || result.error.includes('401')) {
      return 'Authentication issue. Please check your credentials.';
    }

    if (result.error.includes('404')) {
      return 'Supabase endpoint not found. Please verify your project URL.';
    }

    if (result.error.includes('500') || result.error.includes('502') || result.error.includes('503')) {
      return 'Server error. The Supabase service may be experiencing issues.';
    }

    return 'Connection failed. Please try again or contact support.';
  }
}

export const createDiagnostics = (supabaseUrl: string) => {
  return new ConnectionDiagnostics(supabaseUrl);
};
